<template>
  <div class="apartment-activity-timer">
    <ApplicationModal
      v-if="showModal"
      :title="textTitle"
    >
      {{ textContent }}

      <div class="apartment-activity-timer__timer">
        {{ timeLeftDisplay }}
      </div>

      <template v-slot:actions="slot">
        <button
          class="btn btn--large btn--outline"
          :class="slot.classAction"
          @click="reset"
        >
          {{ textReset }}
        </button>
        <button
          class="btn btn--large"
          :class="slot.classAction"
          @click="updateTimeLastActivity"
        >
          {{ textCancel }}
        </button>
      </template>
    </ApplicationModal>
  </div>
</template>

<script>
import {DateTime, Duration} from 'luxon'

import utils from './mixins/utils';
import ApplicationModal from './components/ApplicationModal';
import applicationReset from '../lib/applicationReset';

const localStorageKey = 'application-activity-time';

export default {
  mixins: [
    utils.translate,
  ],
  components: {
    ApplicationModal,
  },
  props: {
    timeToModal: {
      type: Number,
      required: true,
    },
    timeToReset: {
      type: Number,
      required: true,
    },
    textTitle: {
      type: String,
      required: true,
    },
    textContent: {
      type: String,
      required: true,
    },
    textReset: {
      type: String,
      required: true,
    },
    textCancel: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      showModal: false,
      timeLeft: null,
    };
  },
  computed: {
    applicationStateChangeIndicator() {
      // Watch for changes in these properties
      return [
        this.$store.state.favourites,
        this.$store.state.localSave,
      ];
    },
    timeLeftDisplay() {
      const time = +this.timeLeft < 0
        ? Duration.fromObject({})
        : this.timeLeft;
      return +time > 60 * 1000
        ? time.toFormat("m'min' s's'")
        : time.toFormat("s's'");
    },
  },
  watch: {
    applicationStateChangeIndicator: {
      deep: true,
      handler() {
        this.updateTimeLastActivity();
      },
    }
  },
  beforeMount() {
    // First of all, check if the time has run out while the site was not open.
    const timeReset = this.getTimeLastActivity().plus(Duration.fromObject({
      seconds: this.timeToModal + this.timeToReset
    }));
    const timeNow = DateTime.local();
    if (+timeNow > +timeReset) {
      this.reset();
    }

    // Page load counts as activity.
    this.updateTimeLastActivity();

    // Set up a listener to watch for changes in other tabs.
    window.addEventListener('storage', (event) => {
      if (event.key !== localStorageKey) {
        return;
      }
      const time = this.getTimeLastActivity();
      this.onActivity(time);
    }, false);
  },
  methods: {
    getTimeLastActivity() {
      return DateTime.fromISO(localStorage.getItem(localStorageKey));
    },
    updateTimeLastActivity() {
      const now = DateTime.local();
      localStorage.setItem(localStorageKey, now.toISO());
      this.onActivity(now);
    },
    shouldCancelActivityFlow(time) {
      const thereHasBeenActivityDuringWait = +DateTime.fromISO(localStorage.getItem(localStorageKey)) > +time;
      // Load values straight from localStorage (vs Vuex store) to account for possible changes from other browser tabs.
      const localSaveExists = (
        localStorage.getItem('application[right-of-occupancy]')
        ||
        localStorage.getItem('application[rental]')
        ||
        localStorage.getItem('application[owner-occupancy]')
      );
      return !localSaveExists || thereHasBeenActivityDuringWait;
    },
    // Called when the time is updated either on this browser tab (updateTimeLastActivity) or on another (the 'storage'
    // event).
    async onActivity(timeActivity) {
      const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

      this.showModal = false;

      await wait(this.timeToModal * 1000);
      if (this.shouldCancelActivityFlow(timeActivity)) {
        return;
      }

      this.showModal = true;

      // Run through some hoops to keep displayed time up to date.
      const timeReset = timeActivity.plus(Duration.fromObject({
        seconds: this.timeToModal + this.timeToReset
      }));
      let timeNow = null;
      do {
        timeNow = DateTime.local();
        this.timeLeft = timeReset.diff(timeNow);
        const nextUpdate = (+timeReset - +timeNow) % 1000 + 1; // +1 to change the range from 0-999 to 1-1000
        await wait(nextUpdate);
        if (this.shouldCancelActivityFlow(timeActivity)) {
          return;
        }
      } while (+timeReset > +timeNow);

      this.reset();
    },
    reset() {
      applicationReset.resetAll();
    },
  }
}
</script>


<style lang="scss">
@import "~styles/definitions";

.apartment-activity-timer {
}

.apartment-activity-timer__timer {
  @include font-h-22;
  margin-top: r(36);
  text-align: center;
}
</style>
